import React, { useState, useRef, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Statistic,
  Select,
  Space,
  Typography,
  Progress,
  Empty,
  Spin,
  Tooltip,
  Button,
} from "antd";
import { useAppDispatch, useAppSelector } from "../../store";
import styled from "styled-components";
import theme from "../../themes";
import {
  getCallStatsThunk,
  getDashboardDataThunk,
} from "../../store/actions/dashboard.action";
import dayjs from "dayjs";
import { ReactComponent as ArrowDownIcon } from "../../assets/icons/arrowDown.svg";
import {
  formatNumber,
  formatSecondsToTime,
} from "../../utils/formatDecimalNumber";
import { combinePhoneNumberStats } from "../../utils/phoneNumberUtils";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const { Title, Text } = Typography;
const { Option } = Select;

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const { data, callStats } = useAppSelector((state) => state.dashboard);
  const [selectedTimeRange, setSelectedTimeRange] = useState("30d");
  const [selectedFilterBy, setSelectedFilterBy] = useState("agent");
  const [selectedDirection, setSelectedDirection] = useState("outbound");
  const { loading } = useAppSelector((state) => state.dashboard);
  const { user } = useAppSelector((state) => state.auth);
  const navigate = useNavigate();

  const fetchDashboardData = async () => {
    try {
      await dispatch(
        getDashboardDataThunk({
          start_time: getStartDate(selectedTimeRange),
        })
      ).unwrap();
    } catch (error) {
      console.log(error);
    }
  };

  const fetchCallStats = async () => {
    try {
      await dispatch(
        getCallStatsThunk({
          direction: selectedDirection,
          limit: 10000,
          page: 1,
          filter_by: selectedFilterBy,
          start_time: getStartDate(selectedTimeRange),
        })
      ).unwrap();
    } catch (error) {
      console.log(error);
    }
  };

  const getStartDate = (timeRange: string): string => {
    const now = dayjs();
    switch (timeRange) {
      case "24h":
        return now.subtract(1, "day").format("YYYY-MM-DD");
      case "7d":
        return now.subtract(7, "day").format("YYYY-MM-DD");
      case "30d":
        return now.subtract(30, "day").format("YYYY-MM-DD");
      case "90d":
        return now.subtract(90, "day").format("YYYY-MM-DD");
      default:
        return now.subtract(1, "day").format("YYYY-MM-DD");
    }
  };

  const getProvisionedMinutesForRange = (
    timeRange: string,
    monthlyMinutes: number
  ): number => {
    switch (timeRange) {
      case "24h":
      case "7d":
      case "30d":
        return monthlyMinutes; // treat up to 30 days as 1 month
      case "90d":
        return monthlyMinutes * 3; // 3 months
      default:
        return monthlyMinutes;
    }
  };

  useEffect(() => {
    if (!menuOpen) return;
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  useEffect(() => {
    fetchDashboardData();
  }, [selectedTimeRange]);

  useEffect(() => {
    fetchCallStats();
  }, [selectedFilterBy, selectedDirection, selectedTimeRange]);

  // Dynamic stat cards from dashboard data
  const statCards = [
    {
      title: t("dashboard.stats.numberOfCalls"),
      value: data.number_of_calls ?? 0,
    },
    {
      title: t("dashboard.stats.averageDuration"),
      value: formatSecondsToTime(
        data.total_seconds / data.number_of_calls || 0
      ),
    },
    {
      title: t("dashboard.stats.completionRate"),
      value: data.completion_rate
        ? `${formatNumber(data.completion_rate * 100)}%`
        : "0%",
    },
    {
      title: t("dashboard.stats.numberOfCampaigns"),
      value: data.number_of_campaigns ?? 0,
    },
  ];

  // Dynamic summary cards from dashboard data
  const summaryCards = [
    {
      title: t("dashboard.summary.inboundCalls"),
      calls: data.inbound?.count ?? 0,
      completed: (data.inbound?.completion_rate ?? 0) * 100,
      color: "#1890ff",
    },
    {
      title: t("dashboard.summary.outboundCalls"),
      calls: data.outbound?.count ?? 0,
      completed: (data.outbound?.completion_rate ?? 0) * 100,
      color: "#52c41a",
    },
  ];

  // Combine phone numbers with same digits but different formatting
  const combinedCallStats =
    selectedFilterBy === "phone"
      ? combinePhoneNumberStats(callStats)
      : callStats;

  return (
    <DashboardContainer>
      {/* Header */}
      <DashboardHeader justify="space-between" align="middle">
        <Col>
          <Title style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>
            {t("dashboard.title")}
          </Title>
        </Col>
        <Col>
          <HeaderControls>
            <ResponsiveSelect
              size="large"
              defaultValue="30d"
              value={selectedTimeRange}
              onChange={(value: unknown) =>
                setSelectedTimeRange(value as string)
              }
            >
              <Option value="24h">{t("dashboard.filters.last24h")}</Option>
              <Option value="7d">{t("dashboard.filters.last7d")}</Option>
              <Option value="30d">{t("dashboard.filters.last30d")}</Option>
              <Option value="90d">{t("dashboard.filters.last90d")}</Option>
            </ResponsiveSelect>
            {user?.is_super_admin && (
              <SButton
                onClick={() => navigate("/company-selection")}
                type="text"
              >
                {t("dashboard.switchCompany")}
              </SButton>
            )}
          </HeaderControls>
        </Col>
      </DashboardHeader>

      {/* Stat Cards */}
      <Row gutter={[16, 16]}>
        {statCards.map((card, index) => (
          <Col xs={24} sm={12} lg={6} key={index}>
            <RoundedCard>
              <Statistic
                title={
                  <Space>
                    <StatTitle type="secondary">{card.title}</StatTitle>
                  </Space>
                }
                value={card.value}
                valueStyle={{ fontSize: "32px", fontWeight: 700 }}
              />
            </RoundedCard>
          </Col>
        ))}
      </Row>

      {/* Summary Cards */}
      <Row gutter={[16, 16]}>
        {summaryCards.map((card, index) => (
          <Col xs={24} lg={12} key={index}>
            <RoundedCard>
              <Row justify="space-between" align="middle" style={{ gap: 24 }}>
                <SummaryTitle type="secondary">{card.title}</SummaryTitle>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <SummaryCalls strong>{card.calls}</SummaryCalls>
                    <SummaryCallsLabel type="secondary">
                      {t("dashboard.summary.calls")}
                    </SummaryCallsLabel>
                  </div>
                  <div style={{ flex: 1 }}>
                    <SummaryCompleted strong>
                      {formatNumber(card.completed)}%
                    </SummaryCompleted>
                    <SummaryCompletedLabel type="secondary">
                      {t("dashboard.summary.completed")}
                    </SummaryCompletedLabel>
                  </div>
                </div>
              </Row>
            </RoundedCard>
          </Col>
        ))}
      </Row>

      {/* Charts Section */}
      <Row gutter={[16, 16]}>
        {/* Top Number of Calls */}
        <Col xs={24} lg={12}>
          <ChartCard
            title={t("dashboard.charts.topNumberOfCalls")}
            extra={
              <ChartControls>
                <ChartSelect
                  defaultValue={selectedFilterBy}
                  placeholder={t("dashboard.charts.byAgent")}
                  style={{ minWidth: 120 }}
                  suffixIcon={<ArrowDownIcon width={12} height={12} />}
                  options={[
                    { value: "agent", label: t("dashboard.charts.byAgent") },
                    {
                      value: "phone",
                      label: t("dashboard.charts.byPhoneNumber"),
                    },
                  ]}
                  onChange={(value: unknown) => {
                    setSelectedFilterBy(value as string);
                  }}
                />
                <ChartSelect
                  defaultValue={selectedDirection}
                  placeholder={t("dashboard.charts.selectType")}
                  style={{ width: 120 }}
                  suffixIcon={<ArrowDownIcon width={12} height={12} />}
                  options={[
                    {
                      value: "outbound",
                      label: t("dashboard.charts.outbound"),
                    },
                    { value: "inbound", label: t("dashboard.charts.inbound") },
                  ]}
                  onChange={(value: unknown) => {
                    setSelectedDirection(value as string);
                  }}
                />
              </ChartControls>
            }
          >
            {loading && (
              <LoadingOverlay>
                <Spin />
              </LoadingOverlay>
            )}
            {!loading && (
              <TopCallsList>
                {combinedCallStats.length === 0 && (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <EmptyStateTitle>{t("common.noData")}</EmptyStateTitle>
                    }
                  />
                )}
                {combinedCallStats.map((agent, idx) => {
                  const percent = Math.round(
                    (agent.count / combinedCallStats[0].count) * 100
                  );
                  return (
                    <TopCallsItem key={idx}>
                      <Row justify="space-between" align="middle">
                        <Col span={6}>
                          <Tooltip title={agent.label} placement="topLeft">
                            <AgentName>{agent.label}</AgentName>
                          </Tooltip>
                        </Col>
                        <Col
                          span={18}
                          style={{ display: "flex", alignItems: "center" }}
                        >
                          <CustomProgressBarContainer>
                            <CustomProgressBarFill
                              percent={percent}
                              color={theme.colors.green1}
                            />
                            <CustomProgressBarValue color={theme.colors.green1}>
                              {agent.count}
                            </CustomProgressBarValue>
                          </CustomProgressBarContainer>
                        </Col>
                      </Row>
                    </TopCallsItem>
                  );
                })}
              </TopCallsList>
            )}
          </ChartCard>
        </Col>

        {/* Top Costs Pie Chart */}
        <Col xs={24} lg={12}>
          <ChartCard title={t("dashboard.charts.minutesConsumed")}>
            <MinutesChartContainer>
              <Progress
                type="circle"
                percent={
                  data.minutes_provisioned
                    ? Math.round(
                        (data.total_minutes /
                          getProvisionedMinutesForRange(
                            selectedTimeRange,
                            data.minutes_provisioned
                          )) *
                          100
                      )
                    : 0
                }
                format={() => (
                  <ProgressFormatContainer>
                    <ProgressFormatTitle>
                      {t("dashboard.charts.totalMinutes")}
                    </ProgressFormatTitle>
                    <ProgressFormatValue>
                      {formatNumber(data.total_minutes)}
                    </ProgressFormatValue>
                  </ProgressFormatContainer>
                )}
                strokeColor={theme.colors.yellow1}
                trailColor={theme.colors.grey2}
                size={220}
              />
              <MinutesChartLegend>
                <MinutesLegendItem>
                  <MinutesLegendConsumed>
                    <LegendDot
                      style={{ backgroundColor: theme.colors.yellow1 }}
                    />
                    {t("dashboard.charts.minutesConsumedLegend")} -
                    <span style={{ color: "#000", marginLeft: 4 }}>
                      {formatNumber(data.total_minutes)} mins
                    </span>
                  </MinutesLegendConsumed>
                </MinutesLegendItem>
                <MinutesLegendItem>
                  <MinutesLegendProvisioned>
                    <LegendDot
                      style={{ backgroundColor: theme.colors.grey2 }}
                    />
                    {t("dashboard.charts.minutesProvisionedLegend")} -
                    <span style={{ color: "#000", marginLeft: 4 }}>
                      {getProvisionedMinutesForRange(
                        selectedTimeRange,
                        data.minutes_provisioned
                      )}{" "}
                      mins
                    </span>
                  </MinutesLegendProvisioned>
                </MinutesLegendItem>
              </MinutesChartLegend>
            </MinutesChartContainer>
          </ChartCard>
        </Col>
      </Row>
    </DashboardContainer>
  );
};

// Responsive styled components
const DashboardContainer = styled.div`
  min-height: 100vh;
  background-color: ${theme.colors.grey3};
  padding: 52px 72px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    "Helvetica Neue", Arial, sans-serif;
  box-sizing: border-box;
  gap: 24px;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    padding: 16px;
  }

  @media (max-width: 480px) {
    padding: 12px;
  }
`;

const DashboardHeader = styled(Row)``;

const HeaderControls = styled(Space)`
  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
  }

  @media (max-width: 480px) {
    width: 100%;
    gap: 12px;
  }
`;

const ResponsiveSelect = styled(Select)`
  @media (max-width: 768px) {
    width: 140px !important;
  }

  @media (max-width: 480px) {
    width: 100% !important;
  }
`;

const ChartCard = styled(Card)`
  height: 460px;
  border-radius: 12px !important;

  .ant-card-head {
    border-bottom: none;
  }

  .ant-card-head-title {
    font-size: 16px;
    font-weight: 600;
  }

  @media (max-width: 768px) {
    .ant-card-head-extra {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
  }
`;

const RoundedCard = styled(Card)`
  border-radius: 12px !important;
`;

const ChartControls = styled(Space)`
  @media (max-width: 768px) {
    width: 100%;
    gap: 8px;
  }
`;

const ChartSelect = styled(Select)`
  @media (max-width: 1024px) {
    width: 100%;
  }

  // Remove border and background color
  .ant-select-selector {
    border: none !important;
    background: transparent !important;
    box-shadow: none !important;
  }

  .ant-select-arrow {
    color: ${theme.colors.grey1};
  }

  .ant-select-selection-item {
    font-size: 16px;
  }
`;

const EmptyStateTitle = styled.div`
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 8px;
`;

const CustomProgressBarContainer = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  align-items: center;
  border-radius: 8px;
  height: 10px;
`;

const CustomProgressBarFill = styled.div<{ percent: number; color: string }>`
  background: ${(props) => props.color};
  width: ${(props) => props.percent}%;
  height: 100%;
  border-radius: 8px;
  transition: width 0.3s;
`;

const CustomProgressBarValue = styled.span`
  margin-left: 8px;
  font-weight: 400;
  color: #000;
  font-size: 16px;
  line-height: 16px;
`;

// Add new styled components for previously inline-styled elements

const StatTitle = styled(Text)`
  font-weight: 500;
  font-size: 16px;
`;

const StatValue = styled.span`
  font-size: 32px;
  font-weight: 700;
`;

const SummaryTitle = styled(Text)`
  font-size: 16px;
  font-weight: 500;
`;

const SummaryCalls = styled(Text)`
  font-size: 24px;
  font-weight: 500;
`;

const SummaryCallsLabel = styled(Text)`
  margin-left: 4px;
  font-size: 16px;
  font-weight: 400;
`;

const SummaryCompleted = styled(Text)`
  font-size: 24px;
  font-weight: 500;
`;

const SummaryCompletedLabel = styled(Text)`
  margin-left: 4px;
  font-size: 16px;
  font-weight: 400;
`;

const TopCallsList = styled.div`
  margin-bottom: 16px;
  overflow-y: auto;
  padding-right: 16px;
  height: 360px;
`;

const TopCallsItem = styled.div`
  margin-bottom: 12px;
`;

const AgentName = styled(Text)`
  color: ${theme.colors.grey1};
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 90%;
`;

const MinutesChartContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 300px;
  justify-content: center;
`;

const MinutesChartLegend = styled.div`
  margin-top: 24px;
`;

const MinutesLegendItem = styled.div`
  font-weight: 500;
`;

const MinutesLegendConsumed = styled.span`
  color: ${theme.colors.grey1};
  font-weight: 400;
  font-size: 14px;
  display: flex;
  align-items: center;
`;

const MinutesLegendProvisioned = styled.span`
  color: ${theme.colors.grey1};
  font-weight: 400;
  font-size: 14px;
  display: flex;
  align-items: center;
`;

const ProgressFormatContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ProgressFormatTitle = styled.div`
  font-size: 16px;
  font-weight: 500;
`;

const ProgressFormatValue = styled.div`
  font-size: 32px;
  font-weight: 700;
`;

const LegendDot = styled.div`
  display: inline-block;
  width: 16px;
  height: 12px;
  border-radius: 10px;
  margin-right: 8px;
`;

const LoadingOverlay = styled.div`
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  width: 100%;
`;

const SButton = styled(Button)`
  background: ${theme.colors.green1};
  color: #fff;
  border: none;
  border-radius: 8px;
  height: 40px;
  padding: 0 24px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: #047857 !important;
    color: #fff !important;
  }
`;

export default Dashboard;
